import React, { useState, useContext } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import TurnstileWidget from '../components/TurnstileWidget';
import api from '../services/api';
import { LangContext } from '../App';
import { useAuth } from '../contexts/AuthContext';

const T = {
  fr: {
    title: 'Connexion',
    email: 'Adresse courriel',
    motDePasse: 'Mot de passe',
    seConnecter: 'Se connecter',
    chargement: 'Connexion en cours…',
    titre2FA: 'Authentification à deux facteurs',
    desc2FA:
      "Entrez le code à 6 chiffres généré par votre application d'authentification (Google Authenticator, Authy…).",
    code2FA: 'Code à 6 chiffres',
    verifier: 'Vérifier',
    verificationEnCours: 'Vérification…',
    titre2FASetup: 'Configuration 2FA obligatoire',
    desc2FASetup:
      "Votre rôle requiert l'authentification à deux facteurs. Scannez ce QR code avec Google Authenticator ou Authy, puis entrez le code généré.",
    activer: 'Activer et continuer',
    activation: 'Activation…',
    titreCodes: 'Codes de récupération',
    descCodes:
      "Conservez ces codes en lieu sûr. Ils vous permettront d'accéder à votre compte si vous perdez votre téléphone.",
    continuer: 'Continuer vers la connexion',
    emailNonVerifieMsg:
      'Veuillez confirmer votre address courriel avant de vous connecter. Vérifiez votre boîte de réception.',
    emailVerifieNotif: 'Courriel confirmé ! Vous pouvez maintenant vous connecter.',
    renvoyerEmail: 'Renvoyer le courriel de vérification',
    renvoyerEnCours: 'Envoi en cours…',
    renvoyerSucces: 'Courriel renvoyé ! Vérifiez votre boîte de réception (et vos indésirables).',
    errGenerique: 'Une erreur est survenue. Veuillez réessayer.',
    pasDeCompte: 'Pas encore de compte ?',
    sInscrire: "S'inscrire",
    motDePasseOublie: 'Mot de passe oublié ?',
    retour: 'Retour',
  },
  en: {
    title: 'Sign in',
    email: 'Email address',
    motDePasse: 'Password',
    seConnecter: 'Sign in',
    chargement: 'Signing in…',
    titre2FA: 'Two-factor authentication',
    desc2FA:
      'Enter the 6-digit code generated by your authenticator app (Google Authenticator, Authy…).',
    code2FA: '6-digit code',
    verifier: 'Verify',
    verificationEnCours: 'Verifying…',
    titre2FASetup: 'Required 2FA setup',
    desc2FASetup:
      'Your role requires two-factor authentication. Scan this QR code with Google Authenticator or Authy, then enter the generated code.',
    activer: 'Enable and continue',
    activation: 'Enabling…',
    titreCodes: 'Recovery codes',
    descCodes:
      'Keep these codes in a safe place. They will let you access your account if you lose your phone.',
    continuer: 'Continue to sign in',
    emailNonVerifieMsg: 'Please confirm your email address before signing in. Check your inbox.',
    emailVerifieNotif: 'Email confirmed! You can now sign in.',
    renvoyerEmail: 'Resend verification email',
    renvoyerEnCours: 'Sending…',
    renvoyerSucces: 'Email resent! Check your inbox (and spam folder).',
    errGenerique: 'An error occurred. Please try again.',
    pasDeCompte: 'No account yet?',
    sInscrire: 'Sign up',
    motDePasseOublie: 'Forgot your password?',
    retour: 'Back',
  },
};

const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: '6px',
  color: 'var(--couleur-texte)',
  padding: '0.65rem 0.85rem',
  fontSize: '0.9rem',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  color: 'rgba(255,255,255,0.55)',
  marginBottom: '0.35rem',
  letterSpacing: '0.03em',
};

function Champ({ label, children }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function CodeInput({ value, onChange, placeholder }) {
  return (
    <input
      type="text"
      inputMode="numeric"
      maxLength={6}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
      placeholder={placeholder}
      style={{
        ...inputStyle,
        fontSize: '1.4rem',
        letterSpacing: '0.35em',
        textAlign: 'center',
        padding: '0.75rem',
      }}
      onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
    />
  );
}

export default function Connexion() {
  const { lang } = useContext(LangContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { connecter } = useAuth();
  const t = T[lang];

  const emailVerifieNotif = searchParams.get('email_verifie') === '1';

  // Step 1
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);
  const [emailNonVerifie, setEmailNonVerifie] = useState(false);
  const [renvoyerEnCours, setRenvoyerEnCours] = useState(false);
  const [renvoyerSucces, setRenvoyerSucces] = useState(false);
  // Cloudflare Turnstile — token résolu côté widget, requis au submit
  const [turnstileToken, setTurnstileToken] = useState(null);

  // Steps 2 & 3
  const [etape, setEtape] = useState(1);
  const [tempToken, setTempToken] = useState(null);
  const [code2FA, setCode2FA] = useState('');
  const [chargement2FA, setChargement2FA] = useState(false);
  const [erreur2FA, setErreur2FA] = useState('');

  // Step 3 only
  const [qrCode, setQrCode] = useState(null);
  const [codesRecuperation, setCodesRecuperation] = useState(null);

  const handleRenvoyerVerification = async () => {
    if (renvoyerEnCours || renvoyerSucces) return;
    setRenvoyerEnCours(true);
    try {
      await api.post('/auth/renvoyer-verification', { email });
      setRenvoyerSucces(true);
    } catch {
      // réponse intentionnellement générique côté serveur
      setRenvoyerSucces(true);
    } finally {
      setRenvoyerEnCours(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setChargement(true);
    setErreur('');
    setEmailNonVerifie(false);
    setRenvoyerSucces(false);
    try {
      const res = await api.post('/auth/login', {
        email,
        password_hash: motDePasse,
        turnstile_token: turnstileToken,
      });
      if (res.data.token) {
        connecter(res.data.token, res.data.utilisateur);
        navigate('/dashboard');
      } else if (res.data.requires2FA) {
        setTempToken(res.data.tempToken);
        setEtape(2);
      } else if (res.data.requires2FASetup) {
        setTempToken(res.data.tempToken);
        try {
          const qrRes = await api.post(
            '/auth/2fa/setup',
            {},
            {
              headers: { Authorization: `Bearer ${res.data.tempToken}` },
            }
          );
          setQrCode(qrRes.data.qr_code);
        } catch {
          /* non-blocking */
        }
        setEtape(3);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'EMAIL_NON_VERIFIE') {
        setEmailNonVerifie(true);
      } else {
        setErreur(data?.error || t.errGenerique);
      }
    } finally {
      setChargement(false);
      // Reset le token Turnstile (un token n'est utilisable qu'une fois).
      setTurnstileToken(null);
    }
  };

  const handleVerifier2FA = async () => {
    if (code2FA.length !== 6) return;
    setChargement2FA(true);
    setErreur2FA('');
    try {
      const res = await api.post('/auth/2fa/verifier', { tempToken, token: code2FA });
      connecter(res.data.token, res.data.utilisateur);
      navigate('/dashboard');
    } catch (err) {
      setErreur2FA(err.response?.data?.error || t.errGenerique);
    } finally {
      setChargement2FA(false);
    }
  };

  const handleActiver2FA = async () => {
    if (code2FA.length !== 6) return;
    setChargement2FA(true);
    setErreur2FA('');
    try {
      const res = await api.post(
        '/auth/2fa/activer',
        { token: code2FA },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      setCodesRecuperation(res.data.codes_recuperation);
    } catch (err) {
      setErreur2FA(err.response?.data?.error || t.errGenerique);
    } finally {
      setChargement2FA(false);
    }
  };

  const retourStep1 = () => {
    setEtape(1);
    setCode2FA('');
    setErreur2FA('');
    setTempToken(null);
    setQrCode(null);
    setCodesRecuperation(null);
  };

  /* ── Recovery codes screen (step 3 final) ── */
  if (etape === 3 && codesRecuperation) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--couleur-fond)',
          color: 'var(--couleur-texte)',
        }}
      >
        <Header />
        <div style={{ maxWidth: '460px', margin: '4rem auto', padding: '2rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔐</div>
            <h2
              style={{
                fontFamily: 'Georgia, serif',
                color: 'var(--couleur-or)',
                fontSize: '1.4rem',
                margin: 0,
              }}
            >
              {t.titreCodes}
            </h2>
          </div>
          <p
            style={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.85rem',
              marginBottom: '1.2rem',
              lineHeight: 1.6,
            }}
          >
            {t.descCodes}
          </p>
          <div
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '8px',
              padding: '1rem',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0.5rem',
              marginBottom: '1.5rem',
            }}
          >
            {codesRecuperation.map((c, i) => (
              <code
                key={i}
                style={{
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  color: 'var(--couleur-or)',
                  letterSpacing: '0.1em',
                }}
              >
                {c}
              </code>
            ))}
          </div>
          <button
            onClick={retourStep1}
            style={{
              width: '100%',
              background: 'var(--couleur-or)',
              color: 'var(--couleur-fond-carte)',
              border: 'none',
              padding: '0.8rem',
              borderRadius: '6px',
              fontWeight: 700,
              fontSize: '0.95rem',
              cursor: 'pointer',
            }}
          >
            {t.continuer}
          </button>
        </div>
      </div>
    );
  }

  /* ── Step 3: Forced 2FA setup ── */
  if (etape === 3) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--couleur-fond)',
          color: 'var(--couleur-texte)',
        }}
      >
        <Header />
        <div style={{ maxWidth: '460px', margin: '0 auto', padding: '3rem 1.5rem 5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔐</div>
            <h1
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: '1.5rem',
                color: 'var(--couleur-texte)',
                margin: '0 0 0.6rem',
              }}
            >
              {t.titre2FASetup}
            </h1>
            <p
              style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.85rem',
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              {t.desc2FASetup}
            </p>
          </div>

          <div
            style={{
              background: 'rgba(201,162,39,0.06)',
              border: '1px solid rgba(201,162,39,0.25)',
              borderRadius: '10px',
              padding: '1.5rem',
            }}
          >
            {qrCode ? (
              <div style={{ textAlign: 'center', marginBottom: '1.2rem' }}>
                <img
                  src={qrCode}
                  alt="QR Code 2FA"
                  style={{ width: '180px', height: '180px', borderRadius: '8px' }}
                />
              </div>
            ) : (
              <div
                style={{
                  width: '180px',
                  height: '180px',
                  margin: '0 auto 1.2rem',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.2)',
                  fontSize: '0.8rem',
                }}
              >
                QR…
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.6rem', marginBottom: erreur2FA ? '0.5rem' : 0 }}>
              <CodeInput value={code2FA} onChange={setCode2FA} placeholder={t.code2FA} />
              <button
                onClick={handleActiver2FA}
                disabled={chargement2FA || code2FA.length !== 6}
                style={{
                  background: code2FA.length === 6 ? 'var(--couleur-or)' : 'rgba(201,162,39,0.3)',
                  color: 'var(--couleur-fond-carte)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.65rem 1rem',
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  cursor: code2FA.length === 6 ? 'pointer' : 'not-allowed',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {chargement2FA ? t.activation : t.activer}
              </button>
            </div>

            {erreur2FA && (
              <p
                style={{
                  color: 'var(--couleur-texte-erreur)',
                  fontSize: '0.82rem',
                  margin: '0.5rem 0 0',
                }}
              >
                {erreur2FA}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={retourStep1}
            style={{
              marginTop: '1rem',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'rgba(255,255,255,0.5)',
              padding: '0.6rem 1.4rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            ← {t.retour}
          </button>
        </div>
      </div>
    );
  }

  /* ── Step 2: TOTP verification ── */
  if (etape === 2) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--couleur-fond)',
          color: 'var(--couleur-texte)',
        }}
      >
        <Header />
        <div style={{ maxWidth: '420px', margin: '0 auto', padding: '3rem 1.5rem 5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔐</div>
            <h1
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: '1.5rem',
                color: 'var(--couleur-texte)',
                margin: '0 0 0.8rem',
              }}
            >
              {t.titre2FA}
            </h1>
            <p
              style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.85rem',
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              {t.desc2FA}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <CodeInput
              value={code2FA}
              onChange={(val) => {
                setCode2FA(val);
                setErreur2FA('');
              }}
              placeholder={t.code2FA}
            />

            {erreur2FA && (
              <p
                style={{
                  color: 'var(--couleur-texte-erreur)',
                  fontSize: '0.84rem',
                  margin: 0,
                  background: 'rgba(200,50,50,0.1)',
                  border: '1px solid rgba(200,50,50,0.3)',
                  borderRadius: '6px',
                  padding: '0.7rem 1rem',
                }}
              >
                {erreur2FA}
              </p>
            )}

            <button
              onClick={handleVerifier2FA}
              disabled={chargement2FA || code2FA.length !== 6}
              style={{
                background: code2FA.length === 6 ? 'var(--couleur-or)' : 'rgba(201,162,39,0.35)',
                color: 'var(--couleur-fond-carte)',
                border: 'none',
                padding: '0.8rem',
                borderRadius: '6px',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: code2FA.length === 6 ? 'pointer' : 'not-allowed',
                transition: 'background 0.15s',
              }}
            >
              {chargement2FA ? t.verificationEnCours : t.verifier}
            </button>

            <button
              type="button"
              onClick={retourStep1}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.5)',
                padding: '0.65rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              ← {t.retour}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Step 1: Email + password ── */
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--couleur-fond)',
        color: 'var(--couleur-texte)',
      }}
    >
      <Header />
      <div style={{ maxWidth: '420px', margin: '0 auto', padding: '3rem 1.5rem 5rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h1
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: '1.8rem',
              color: 'var(--couleur-texte)',
              margin: '0 0 0.4rem',
            }}
          >
            {t.title}
          </h1>
        </div>

        {/* Email confirmed notification */}
        {emailVerifieNotif && (
          <div
            style={{
              background: 'rgba(40,160,80,0.12)',
              border: '1px solid rgba(40,160,80,0.35)',
              borderRadius: '6px',
              padding: '0.85rem 1rem',
              marginBottom: '1.5rem',
              fontSize: '0.85rem',
              color: 'var(--couleur-texte-succes)',
            }}
          >
            ✓ {t.emailVerifieNotif}
          </div>
        )}

        <form onSubmit={handleLogin} noValidate>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
            <Champ label={t.email}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                style={inputStyle}
                placeholder="marie@exemple.ca"
                onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
                onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
              />
            </Champ>

            <Champ label={t.motDePasse}>
              <input
                type="password"
                value={motDePasse}
                onChange={(e) => setMotDePasse(e.target.value)}
                required
                autoComplete="current-password"
                style={inputStyle}
                placeholder="••••••••"
                onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
                onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
              />
            </Champ>

            {/* Email not verified message + resend button */}
            {emailNonVerifie && (
              <div
                style={{
                  background: 'rgba(201,162,39,0.1)',
                  border: '1px solid rgba(201,162,39,0.3)',
                  borderRadius: '6px',
                  padding: '0.85rem 1rem',
                  fontSize: '0.84rem',
                  color: 'rgba(201,162,39,0.9)',
                  lineHeight: 1.5,
                }}
              >
                <div>📧 {t.emailNonVerifieMsg}</div>
                {renvoyerSucces ? (
                  <div
                    style={{
                      marginTop: '0.6rem',
                      color: 'var(--couleur-texte-succes)',
                      fontSize: '0.82rem',
                    }}
                  >
                    ✓ {t.renvoyerSucces}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleRenvoyerVerification}
                    disabled={renvoyerEnCours}
                    style={{
                      marginTop: '0.7rem',
                      background: 'transparent',
                      border: '1px solid rgba(201,162,39,0.5)',
                      borderRadius: '5px',
                      color: 'rgba(201,162,39,0.9)',
                      padding: '0.4rem 0.9rem',
                      fontSize: '0.8rem',
                      cursor: renvoyerEnCours ? 'not-allowed' : 'pointer',
                      opacity: renvoyerEnCours ? 0.6 : 1,
                    }}
                  >
                    {renvoyerEnCours ? t.renvoyerEnCours : `↩ ${t.renvoyerEmail}`}
                  </button>
                )}
              </div>
            )}

            {/* Generic error */}
            {erreur && (
              <div
                style={{
                  background: 'rgba(200,50,50,0.12)',
                  border: '1px solid rgba(200,50,50,0.35)',
                  borderRadius: '6px',
                  padding: '0.85rem 1rem',
                  fontSize: '0.84rem',
                  color: 'var(--couleur-texte-erreur)',
                }}
              >
                {erreur}
              </div>
            )}

            {/* Cloudflare Turnstile — protection anti-bots, requise au submit */}
            <TurnstileWidget
              siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
              onSuccess={(token) => setTurnstileToken(token)}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
            />

            <button
              type="submit"
              disabled={chargement || !turnstileToken}
              style={{
                background:
                  chargement || !turnstileToken ? 'rgba(201,162,39,0.5)' : 'var(--couleur-or)',
                color: 'var(--couleur-fond-carte)',
                border: 'none',
                padding: '0.8rem',
                borderRadius: '6px',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: chargement || !turnstileToken ? 'not-allowed' : 'pointer',
                transition: 'filter 0.15s',
                marginTop: '0.3rem',
              }}
            >
              {chargement ? t.chargement : t.seConnecter}
            </button>
          </div>
        </form>

        <p
          style={{
            textAlign: 'center',
            marginTop: '2rem',
            color: 'rgba(255,255,255,0.38)',
            fontSize: '0.84rem',
          }}
        >
          {t.pasDeCompte}{' '}
          <Link to="/signup" style={{ color: 'var(--couleur-or)', textDecoration: 'none' }}>
            {t.sInscrire}
          </Link>
        </p>

        <p
          style={{
            textAlign: 'center',
            marginTop: '0.5rem',
            color: 'rgba(255,255,255,0.38)',
            fontSize: '0.84rem',
          }}
        >
          <Link
            to="/forgot-password"
            style={{ color: 'var(--couleur-or)', textDecoration: 'none' }}
          >
            {t.motDePasseOublie}
          </Link>
        </p>
      </div>
    </div>
  );
}
